/**
 * Snapshot of the INITIAL `location.search` (deep link).
 *
 * Problem: when loading the app through a deep URL with search params
 * (`/bible/kjv/gen/1?strong=H430`, `/search?q=…`), TanStack Router defers its first
 * navigation "commit" (microtask `flush` from `@tanstack/history`) and, during
 * startup, the URL can be rewritten losing the search params before the destination
 * route gets to read them. The result: the deep link is "lost" during boot and
 * the view opens without its shareable state.
 *
 * Solution: we capture the search params from the **first** `location.search`
 * SYNCHRONOUSLY at module import (before the router mounts and flushes). Routes use
 * this snapshot to seed their state and, on first mount, rewrite the params into
 * the URL (with `replace`) if boot lost them. The snapshot is consumed ONCE per key:
 * after reading it is deleted, so later internal navigations never revive an old
 * value (idempotence).
 */

const initialParams = new URLSearchParams(
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.search : "",
);

/** Route (pathname) where the app loaded, to avoid applying the snapshot outside it. */
const initialPath =
  typeof globalThis !== "undefined" && globalThis.location ? globalThis.location.pathname : "";

/**
 * Returns (and CONSUMES) the initial value of a startup deep-link search param.
 * It returns it only if the app loaded in `pathPrefix` (e.g. `/bible/`, `/search`,
 * `/dictionary`), so each route restores only its own. After reading, it removes it
 * from the snapshot: a second read (or an internal navigation) returns `null`.
 */
export function takeInitialSearchParam(key: string, pathPrefix: string): string | null {
  if (!initialPath.startsWith(pathPrefix)) {
    return null;
  }
  const value = initialParams.get(key);
  if (value === null) {
    return null;
  }
  initialParams.delete(key);
  return value;
}
